// Pupil Special Requirements Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import type { Pupil, SpecialRequirement } from '../../../../types';

const CATEGORIES: SpecialRequirement['category'][] = ['sen', 'medical', 'dietary', 'accessibility', 'behavioral'];
const PRIORITIES: SpecialRequirement['priority'][] = ['high', 'medium', 'low'];

const getDefaultRequirement = (): Omit<SpecialRequirement, 'id'> => ({
  category: 'sen',
  description: '',
  priority: 'medium',
  accommodations: [],
});

interface SpecialRequirementsTabProps {
  pupil: Pupil;
  onUpdate?: (updates: Partial<Pupil>) => void;
}

const getCategoryColor = (category: SpecialRequirement['category']) => {
  switch (category) {
    case 'sen':
      return 'primary';
    case 'medical':
      return 'error';
    case 'dietary':
      return 'success';
    case 'accessibility':
      return 'info';
    case 'behavioral':
      return 'warning';
    default:
      return 'default';
  }
};

const getCategoryLabel = (category: SpecialRequirement['category']) => {
  switch (category) {
    case 'sen':
      return 'SEN';
    case 'medical':
      return 'Medical';
    case 'dietary':
      return 'Dietary';
    case 'accessibility':
      return 'Accessibility';
    case 'behavioral':
      return 'Behavioral';
    default:
      return category;
  }
};

const SpecialRequirementsTab: FC<SpecialRequirementsTabProps> = ({ pupil, onUpdate }) => {
  const requirements = pupil.specialRequirements || [];
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reqForm, setReqForm] = useState<Omit<SpecialRequirement, 'id'>>(getDefaultRequirement());
  const [editingReqId, setEditingReqId] = useState<string | null>(null);
  const [accommodationInput, setAccommodationInput] = useState('');

  const handleOpenAdd = () => {
    setReqForm(getDefaultRequirement());
    setAccommodationInput('');
    setAddModalOpen(true);
  };

  const handleOpenEdit = (req: SpecialRequirement) => {
    setEditingReqId(req.id);
    setReqForm({
      category: req.category,
      description: req.description,
      priority: req.priority,
      accommodations: req.accommodations || [],
    });
    setAccommodationInput('');
    setEditModalOpen(true);
  };

  const handleAddAccommodation = () => {
    if (accommodationInput.trim()) {
      setReqForm(prev => ({
        ...prev,
        accommodations: [...(prev.accommodations || []), accommodationInput.trim()],
      }));
      setAccommodationInput('');
    }
  };

  const handleRemoveAccommodation = (index: number) => {
    setReqForm(prev => ({
      ...prev,
      accommodations: prev.accommodations?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleAddRequirement = () => {
    if (reqForm.description) {
      const newReq: SpecialRequirement = {
        ...reqForm,
        id: `req-${Date.now()}`,
      };
      onUpdate?.({ specialRequirements: [...requirements, newReq] });
      setAddModalOpen(false);
      setReqForm(getDefaultRequirement());
    }
  };

  const handleEditRequirement = () => {
    if (editingReqId && reqForm.description) {
      const updatedReqs = requirements.map(r =>
        r.id === editingReqId ? { ...r, ...reqForm } : r
      );
      onUpdate?.({ specialRequirements: updatedReqs });
      setEditModalOpen(false);
      setEditingReqId(null);
      setReqForm(getDefaultRequirement());
    }
  };

  // Group by category
  const groupedRequirements: Record<string, SpecialRequirement[]> = {};
  requirements.forEach((req) => {
    if (!groupedRequirements[req.category]) {
      groupedRequirements[req.category] = [];
    }
    groupedRequirements[req.category].push(req);
  });

  return (
    <Stack spacing={3}>
      {/* Summary Stats */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={2.4}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'primary.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="primary.main">
              {groupedRequirements['sen']?.length || 0}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              SEN
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'error.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="error.main">
              {groupedRequirements['medical']?.length || 0}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Medical
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'success.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="success.main">
              {groupedRequirements['dietary']?.length || 0}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Dietary
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'info.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="info.main">
              {groupedRequirements['accessibility']?.length || 0}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Accessibility
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <DsCard sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'warning.main' }}>
            <DsTypography variant="h4" fontWeight={700} color="warning.main">
              {groupedRequirements['behavioral']?.length || 0}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Behavioral
            </DsTypography>
          </DsCard>
        </Grid>
      </Grid>

      {/* Requirements List */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <DsTypography variant="h6" fontWeight={600}>
            All Requirements
          </DsTypography>
          {onUpdate && (
            <DsButton variant="outlined" size="small" startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAdd}>
              Add Requirement
            </DsButton>
          )}
        </Stack>

        {requirements.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Accommodations</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  {onUpdate && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {requirements.map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(req.category)}
                        size="small"
                        color={getCategoryColor(req.category) as 'primary' | 'error' | 'success' | 'info' | 'warning' | 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <DsTypography variant="body2">
                        {req.description}
                      </DsTypography>
                    </TableCell>
                    <TableCell>
                      {req.accommodations && req.accommodations.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {req.accommodations.slice(0, 2).map((acc, idx) => (
                            <Chip key={idx} label={acc} size="small" variant="outlined" />
                          ))}
                          {req.accommodations.length > 2 && (
                            <Chip
                              label={`+${req.accommodations.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      ) : (
                        <DsTypography variant="caption" color="text.secondary">
                          None specified
                        </DsTypography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={req.priority}
                        size="small"
                        color={
                          req.priority === 'high'
                            ? 'error'
                            : req.priority === 'medium'
                              ? 'warning'
                              : 'default'
                        }
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    {onUpdate && (
                      <TableCell align="right">
                        <Tooltip title="Edit Requirement">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(req)}>
                            <DsIcon icon="EDIT" sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsIcon icon="CHECK_CIRCLE" sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <DsTypography variant="h6" color="text.secondary" gutterBottom>
              No Special Requirements
            </DsTypography>
            <DsTypography variant="body2" color="text.secondary">
              This pupil has no documented special requirements.
            </DsTypography>
            {onUpdate && (
              <DsButton variant="contained" size="small" sx={{ mt: 2 }} startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAdd}>
                Add Requirement
              </DsButton>
            )}
          </Box>
        )}
      </DsCard>

      {/* Category Explanations */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Category Definitions
        </DsTypography>
        <Grid container spacing={2}>
          {[
            { category: 'sen', label: 'SEN', description: 'Special Educational Needs - learning difficulties, cognitive support' },
            { category: 'medical', label: 'Medical', description: 'Health conditions, medications, emergency procedures' },
            { category: 'dietary', label: 'Dietary', description: 'Food allergies, intolerances, dietary preferences' },
            { category: 'accessibility', label: 'Accessibility', description: 'Physical access needs, mobility support' },
            { category: 'behavioral', label: 'Behavioral', description: 'Behavioral support plans, interventions' },
          ].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.category}>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Chip
                  label={item.label}
                  size="small"
                  color={getCategoryColor(item.category as SpecialRequirement['category']) as 'primary' | 'error' | 'success' | 'info' | 'warning' | 'default'}
                  sx={{ mb: 1 }}
                />
                <DsTypography variant="body2" color="text.secondary">
                  {item.description}
                </DsTypography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DsCard>

      {/* Add Requirement Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Special Requirement</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={reqForm.category}
                    label="Category"
                    onChange={(e) => setReqForm({ ...reqForm, category: e.target.value as SpecialRequirement['category'] })}
                  >
                    {CATEGORIES.map(c => (
                      <MenuItem key={c} value={c}>
                        <Chip label={getCategoryLabel(c)} size="small" color={getCategoryColor(c) as 'primary' | 'error' | 'success' | 'info' | 'warning' | 'default'} sx={{ mr: 1 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={reqForm.priority}
                    label="Priority"
                    onChange={(e) => setReqForm({ ...reqForm, priority: e.target.value as SpecialRequirement['priority'] })}
                  >
                    {PRIORITIES.map(p => (
                      <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={reqForm.description}
              onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })}
              placeholder="Describe the requirement in detail..."
            />
            <Box>
              <DsTypography variant="subtitle2" gutterBottom>Accommodations</DsTypography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add accommodation..."
                  value={accommodationInput}
                  onChange={(e) => setAccommodationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAccommodation()}
                />
                <DsButton variant="outlined" onClick={handleAddAccommodation}>Add</DsButton>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {reqForm.accommodations?.map((acc, idx) => (
                  <Chip key={idx} label={acc} size="small" onDelete={() => handleRemoveAccommodation(idx)} />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAddModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleAddRequirement} disabled={!reqForm.description}>Add Requirement</DsButton>
        </DialogActions>
      </Dialog>

      {/* Edit Requirement Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Special Requirement</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={reqForm.category}
                    label="Category"
                    onChange={(e) => setReqForm({ ...reqForm, category: e.target.value as SpecialRequirement['category'] })}
                  >
                    {CATEGORIES.map(c => (
                      <MenuItem key={c} value={c}>
                        <Chip label={getCategoryLabel(c)} size="small" color={getCategoryColor(c) as 'primary' | 'error' | 'success' | 'info' | 'warning' | 'default'} sx={{ mr: 1 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={reqForm.priority}
                    label="Priority"
                    onChange={(e) => setReqForm({ ...reqForm, priority: e.target.value as SpecialRequirement['priority'] })}
                  >
                    {PRIORITIES.map(p => (
                      <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={reqForm.description}
              onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })}
              placeholder="Describe the requirement in detail..."
            />
            <Box>
              <DsTypography variant="subtitle2" gutterBottom>Accommodations</DsTypography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add accommodation..."
                  value={accommodationInput}
                  onChange={(e) => setAccommodationInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAccommodation()}
                />
                <DsButton variant="outlined" onClick={handleAddAccommodation}>Add</DsButton>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {reqForm.accommodations?.map((acc, idx) => (
                  <Chip key={idx} label={acc} size="small" onDelete={() => handleRemoveAccommodation(idx)} />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleEditRequirement} disabled={!reqForm.description}>Save Changes</DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SpecialRequirementsTab;

